/**
 * Builds an export file (ADR 0018) in a Web Worker (falls back to the page, in chunks, when workers are
 * unavailable) and hands it to the browser as a download. The data comes from the provider's export*()
 * calls, which log the export on the server (export_report); nothing is uploaded or stored on a server.
 */
import { REPORT_LIMITS } from "@fts/core";
import { buildFile } from "./build";
import { toBase64 } from "./pdf";
import type { ExportJob } from "./sheet";

export class ExportTooLargeError extends Error {
  constructor(readonly rows: number) {
    super("export_too_large");
    this.name = "ExportTooLargeError";
  }
}

let fontCache: Promise<{ regular: ArrayBuffer; bold: ArrayBuffer }> | null = null;

/** Noto Sans (OFL), served from /fonts, fetched once per session for PDF exports. */
export function loadPdfFonts(base: string = typeof document !== "undefined" ? document.baseURI : "/"): Promise<{ regular: ArrayBuffer; bold: ArrayBuffer }> {
  if (!fontCache) {
    const get = async (name: string) => {
      const res = await fetch(new URL(`fonts/${name}`, base));
      if (!res.ok) throw new Error(`font ${name}: ${res.status}`);
      return res.arrayBuffer();
    };
    fontCache = Promise.all([get("NotoSans-Regular.ttf"), get("NotoSans-Bold.ttf")]).then(([regular, bold]) => ({ regular, bold }));
    fontCache.catch(() => (fontCache = null));
  }
  return fontCache;
}

let logoCache: Promise<string | null> | null = null;

/** The Expartise logo (public/brand) as a PNG data URL for the PDF cover, fetched once; null if unavailable. */
export function loadPdfLogo(base: string = typeof document !== "undefined" ? document.baseURI : "/"): Promise<string | null> {
  if (!logoCache) {
    logoCache = fetch(new URL("brand/expartise-logo.png", base))
      .then(async (res) => (res.ok ? `data:image/png;base64,${toBase64(await res.arrayBuffer())}` : null))
      .catch(() => null);
    logoCache.then((v) => v === null && (logoCache = null));
  }
  return logoCache;
}

function inWorker(job: ExportJob): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const w = new Worker(new URL("./export.worker.ts", import.meta.url), { type: "module" });
    w.onmessage = (e: MessageEvent<{ ok: boolean; buffer?: ArrayBuffer; text?: string; mime?: string; error?: string }>) => {
      w.terminate();
      if (e.data.ok) resolve(new Blob([e.data.text ?? e.data.buffer!], { type: e.data.mime }));
      else reject(new Error(e.data.error ?? "export failed"));
    };
    w.onerror = (e) => {
      w.terminate();
      reject(new Error(e.message || "worker error"));
    };
    w.postMessage(job);
  });
}

export async function buildBlob(job: ExportJob, useWorker = typeof Worker !== "undefined"): Promise<Blob> {
  if (useWorker) {
    try {
      return await inWorker(job);
    } catch {
      // A worker that cannot start (CSP, old browser) must not block the export: build on the page.
    }
  }
  const { data, mime } = await buildFile(job);
  return new Blob([data], { type: mime });
}

export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Build and download a file from rows the server has already logged; refuses above the row cap. */
export async function runExport(job: ExportJob, filename: string): Promise<void> {
  const rows = job.sheets.reduce((n, s) => n + s.rows.length, 0);
  if (job.sheets.some((s) => s.rows.length > REPORT_LIMITS.exportMaxRows)) throw new ExportTooLargeError(rows);
  let final = job;
  if (job.format === "pdf") {
    const [fonts, logo] = await Promise.all([job.fonts ?? loadPdfFonts(), job.logo !== undefined ? job.logo : loadPdfLogo()]);
    final = { ...job, fonts, logo };
  }
  saveBlob(await buildBlob(final), filename);
}
